import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CookiePreferencesModal from "./CookiePreferencesModal";

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSettings, setCurrentSettings] = useState({
    strictlyNecessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem("fozzil_cookie_consent");
    if (!consent) {
      const timer = setTimeout(() => {
        setVisible(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setAnimateIn(true));
        });
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved settings if any
      try {
        const saved = JSON.parse(consent);
        if (typeof saved === 'object') {
          setCurrentSettings(saved);
        }
      } catch (e) {
        // Fallback for old simple string consent
      }
    }
  }, []);

  const handleSave = (settings) => {
    localStorage.setItem("fozzil_cookie_consent", JSON.stringify(settings));
    setCurrentSettings(settings);
    setIsModalOpen(false);
    setAnimateIn(false);
    setTimeout(() => setVisible(false), 300);
  };

  const handleAcceptAll = () => {
    const allSettings = { strictlyNecessary: true, analytics: true, marketing: true };
    handleSave(allSettings);
  };

  const handleEssentialOnly = () => {
    const essentialSettings = { strictlyNecessary: true, analytics: false, marketing: false };
    handleSave(essentialSettings);
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        style={{
          opacity: animateIn ? 1 : 0,
          transform: animateIn ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: animateIn ? "auto" : "none",
        }}
      >
        <div className="max-w-5xl mx-auto bg-[#050505] border border-white/5 shadow-2xl rounded-[32px] overflow-hidden">
          {/* Top accent line */}
          <div className="h-[2px] bg-gradient-to-r from-blue-500/0 via-blue-500/60 to-blue-500/0" />
          
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {/* Logo */}
              <div className="flex-shrink-0">
                <img 
                  src="/assets/varaen-logo.jpg" 
                  alt="FOZZIL VARAEN" 
                  className="w-10 h-10 object-cover rounded-xl border border-[#065DC0]/40 shadow-[0_0_15px_rgba(6,93,192,0.3)]"
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white text-[15px] font-semibold mb-1 tracking-tight">
                  Your Privacy Matters
                </h3>
                <p className="text-gray-400 text-[13px] leading-relaxed m-0">
                  We use essential cookies to make FOZZIL work. We'd also like to set optional cookies to help us 
                  improve your experience and understand how you use our services.{" "}
                  <button 
                    onClick={() => setIsModalOpen(true)}
                    className="text-blue-400 hover:text-blue-300 underline underline-offset-4 transition-colors cursor-pointer font-medium"
                  >
                    Manage Cookies
                  </button>
                  {" "}&bull;{" "}
                  <Link 
                    to="/cookie-policy" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline underline-offset-4 transition-colors"
                  >
                    Cookie Policy
                  </Link>
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto md:flex-shrink-0">
                <div className="flex gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex-1 sm:flex-none px-4 md:px-6 py-2.5 text-[12px] font-bold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all duration-200 cursor-pointer"
                  >
                    Essential
                  </button>
                  <button
                    onClick={handleEssentialOnly}
                    className="flex-1 sm:flex-none px-4 md:px-6 py-2.5 text-[12px] font-bold text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-all duration-200 cursor-pointer"
                  >
                    Reject All
                  </button>
                </div>
                <button
                  onClick={handleAcceptAll}
                  className="w-full sm:w-auto px-7 py-2.5 text-[12px] font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-full transition-all duration-200 cursor-pointer shadow-lg shadow-blue-500/20"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CookiePreferencesModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleSave}
        initialSettings={currentSettings}
      />
    </>
  );
};

export default CookieConsent;
